import { Text } from '@chakra-ui/layout';
import { useColorModeValue, useTheme } from '@chakra-ui/react';
import React from 'react';
import { WattwatchersMeter } from '../../api/api-ww-meter';
import { createCustomCellularIcon, createCustomWifiIcon } from '../../styles/custom-dynamic-icons';
import { getMinuteDifferenceBetweenTimeStamps } from '../../utils/datetime-utils';

const SIGNAL_LEVEL_TO_VALUE: Record<number, string> = {
  4: 'Excellent Connection',
  3: 'Good Connection',
  2: 'Low Connection',
  1: 'Poor Connection',
  0: 'Offline',
};

const WIFI_SIGNAL_RANGES = [-40, -55, -70];
const CELL_SIGNAL_RANGES: Record<string, number[]> = {
  '3G': [-89, -95, -101],
  '4G': [-65, -75, -95],
};

const signalQualityToSignalLevel = (
  signalQualityDbm: number,
  isWifi: boolean,
  cellMode: string | undefined,
  lastHeardInMinutes: number | null
): number => {
  if (
    !lastHeardInMinutes ||
    lastHeardInMinutes > 5 ||
    (!isWifi && !cellMode) ||
    (signalQualityDbm !== 0 && !signalQualityDbm)
  )
    return 0;
  const rangeArray = isWifi ? WIFI_SIGNAL_RANGES : CELL_SIGNAL_RANGES[cellMode || '4G'];

  for (let index = 0; index < rangeArray.length; index++) {
    const currentValue = rangeArray[index];
    const signalLevel = rangeArray.length - index;

    // if last index reached, it means lowest signal
    if (index === rangeArray.length - 1) return signalLevel;

    // if first index and signal quality greater than first value means best signal
    if (index === 0 && signalQualityDbm > currentValue) return signalLevel + 1;

    // otherwise check if its between next and current value
    const nextValue = rangeArray[index + 1];
    if (signalQualityDbm < currentValue && signalQualityDbm >= nextValue) return signalLevel;
  }

  return 0;
};

const CustomSignalIcon = ({ meter, shortenedText = false }: { meter: WattwatchersMeter; shortenedText?: boolean }) => {
  const { signalQualityDbm, mode, type, lastHeardAt } = meter.comms;
  const lastHeardInMinutes = getMinuteDifferenceBetweenTimeStamps(lastHeardAt, meter.timezone);
  const isWifi = type === 'wifi';
  const signalLevel = signalQualityToSignalLevel(signalQualityDbm, isWifi, mode, lastHeardInMinutes);
  let signalQualityValue = SIGNAL_LEVEL_TO_VALUE[signalLevel];
  if (shortenedText) signalQualityValue = signalQualityValue.replace(' Connection', '');
  const theme = useTheme();
  const signalIconColor = useColorModeValue(theme.colors.black, theme.colors.white);
  const SignalIcon = isWifi
    ? createCustomWifiIcon(signalLevel, signalIconColor)
    : createCustomCellularIcon(signalLevel, signalIconColor);

  return (
    <>
      <SignalIcon w={6} h={6} mr={1} />
      <Text fontWeight={600} color={signalIconColor}>{`${signalQualityValue}`}</Text>
    </>
  );
};

export default CustomSignalIcon;
